import classNames from "classnames";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { Item, SummonerSpell } from "@outplayed/tooltips";
import { getRoleLabel } from "@ugg/shared/utils/role-helpers";
import { useGlobal } from "reactn";

interface RoleRewardsProps {
  champID: number;
  questCompletionTime?: [number];
  role: string;
  patch: string;
  className?: string;
}

const formatTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export default function RoleRewards(props: RoleRewardsProps) {
  const { role, champID, questCompletionTime, patch, className } = props;
  const { getChampionImg, getChampionName, getChampionSplashImg } = getRiotAssetsContext();
  const [language] = useGlobal("language");

  const roleQuestText: Record<string, JSX.Element> = {
    top: (
      <div>
        Completing this quest will grant: an upfront flat amount of experience,{" "}
        <span className="font-bold text-white">bonus flat experience on champion takedowns</span>,{" "}
        <span className="font-bold text-white">increased experience gain</span> from all other sources, and increased{" "}
        <span className="font-bold text-white">level cap of up to 20</span>. It will also grant an{" "}
        <span className="font-bold text-white">
          upgraded version of <SummonerSpell className="inline h-[16px] w-[16px]" spellId={12} language={language} ssr webp />{" "}
          Teleport
        </span>{" "}
        or if it wasn't selected as a Summoner Spell it will grant Teleport as a third Summoner Spell.
      </div>
    ),
    jungle: (
      <div>
        Completing this quest will grant:{" "}
        <span className="font-bold text-white">
          upgraded smite <SummonerSpell className="inline h-[16px] w-[16px]" spellId={11} language={language} ssr webp />{" "}
        </span>
        , increased <span className="font-bold text-white">move speed</span> in jungle or river, and increased rewards from large
        monsters.
      </div>
    ),
    mid: (
      <div>
        Completing this quest will grant a free{" "}
        <Item className="inline-block rounded-[3px] overflow-hidden" itemId={3173} spriteSize={16} isSprite ssr patch={patch} />{" "}
        <span className="font-bold text-white">tier 3 boot upgrade</span>, as well as{" "}
        <span className="font-bold text-white">bonus AD and AP</span>.
      </div>
    ),
    adc: (
      <div>
        Completing this quest will grant: flat amount of gold, <span className="font-bold text-white">increased gold gained</span>{" "}
        from minions and champion takedowns, and an effective <span className="font-bold text-white">7th item slot</span>.
      </div>
    ),
    support: (
      <div>
        Completing this quest will grant: <span className="font-bold text-white">upgraded support item</span>, control ward cost
        reduction, dedicated{" "}
        <Item className="inline-block rouned-[3px] overflow-hidden" itemId={2055} spriteSize={16} isSprite ssr patch={patch} />{" "}
        <span className="font-bold text-white">control ward slot</span>.
      </div>
    ),
  };

  return (
    <div className={classNames("relative w-full h-full", className)}>
      <img
        className="absolute inset-0 bg-cover bg-center object-cover object-top w-full h-full opacity-50"
        src={getChampionSplashImg(champID)}
      />
      <div className="absolute inset-0 from-purple-400 to-purple-400/50 bg-gradient-to-r"></div>

      <div className="relative z-10 p-[18px]">
        <div className="relative flex items-center w-full mb-[18px]">
          <span className="absolute left-0 w-[2px] h-[20px] rounded-[2px] bg-accent-blue-400"></span>
          <div className="font-inter text-[14px] font-bold text-white px-[12px]">Role Rewards</div>
          <div className="absolute right-0 rounded-[3px] border border-[#5C463B] bg-purple-200 shadow-[0_2px_4px_rgba(0,0,0,0.25)] ">
            <div className="flex items-center font-inter text-[11px] font-medium text-accent-gray-100">
              <div className="left-0 w-[27px] h-[27px] overflow-hidden rounded-[3px]">
                <img
                  src={getChampionImg(champID, { webp: true })}
                  alt={getChampionName(champID)}
                  className="w-full h-full object-cover transform scale-[1.2]"
                />
              </div>
              <div className="py-1.5 px-3">
                {getChampionName(champID)} {getRoleLabel(role).toLowerCase()} quest completion goal:{" "}
                {questCompletionTime?.[0] !== undefined && (
                  <span className="font-inter text-[11px] font-bold text-white">{formatTime(questCompletionTime[0])}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full text-accent-gray-100 font-inter text-xxs leading-5 font-medium">{roleQuestText[role]}</div>
      </div>
    </div>
  );
}
